<template>
  <!-- Partner -->
  <div id="story-mode-partner">

    <!-- Main Navigation -->
    <main-navigation
      :back="backNavigation"
      :next="nextNavigation"
    />
    <!-- /Main Navigation -->

    <!-- Container -->
    <div class="container-1200">

      <!-- Content -->
      <div
        class="content"
        :class="{ 'position-relative': statusOfCompleted }"
      >
        <v-app>
          <!-- Header -->
          <header class="page-header">

            <!-- Dashboard Header -->
            <dashboard-header
              :class="{ 'stamp': statusOfCompleted }"
              :title="$t('pages.businessGuide.partner.title')"
              :guide-visible="guideVisible"
              :guide-visible-first-time="guideVisibleFirstTime"
              :completed-step-name="'partnerCompleted'"
              hide-quick-tour-button
              @toggle-guide="onGuideExampleVisible"
              @open-comp-dialog="openedDialogProcessCompleted"
              @open-info="openInfo"
              @open-ai-buddy-drawer="aiBuddyDrawerVisible = true"
            />
            <!-- /Dashboard Header -->

            <!-- Divider -->
            <hr>
            <!-- /Divider -->

          </header>
          <!-- /Header -->

          <!-- Info Guide Expand Panel -->
          <info-guide
            v-if="guideVisibleFirstTime"
            class="mb-5"
            type="partnerExamples"
            visible
            @click="guideVisibleFirstTime = !guideVisibleFirstTime"
          />
          <!-- Info Guide Expand Panel -->

          <!-- Ai Buddy Drawer -->
          <ai-buddy-drawer
            :visible="aiBuddyDrawerVisible"
            section-name="Partner"
            help-me-write
            type="partners"
            @close="aiBuddyDrawerVisible = false"
          />
          <!-- /Ai Buddy Drawer -->
        </v-app>

        <!-- Business plan additional info -->
        <business-plan-additional-info
          completed-step-name="partnerCompleted"
          section-name="Partner"
          show-ai-buddy
          :loading-state="aiBuddyLoader"
          @ai-buddy-open="aiBuddyDrawerVisible = true"
        />
        <!-- /Business plan additional info -->

        <!-- Height Holder For Sticky Header -->
        <div class="height-holder d-none d-md-block" />
        <!-- /Height Holder For Sticky Header -->

        <!-- Portfolio -->
        <div class="portfolio">

          <v-app>
            <!-- Header -->
            <business-guide-header>
              <template #left>
                <h2>{{ $t('pages.businessGuide.partner.keyBusinessPartners') }}</h2>
                <p class="text-lead">
                  {{ $t('pages.businessGuide.partner.describeYourMajorPartners') }}
                </p>
              </template>
              <template #right>
                <ai-template-blinking-animation v-if="aiTemplateLoader" />
                <erase-icon-button
                  v-if="removeButtonVisible"
                  @click="onEraseFromTemplate"
                />
                <business-guide-search
                  v-if="!isPartnersEmpty"
                  v-model="searchValue"
                  class="mx-1"
                />
                <!-- View Switcher -->
                <view-switcher
                  v-if="isPartnersEmpty === false"
                  :active-view="partnersView"
                  @activate-view="onActivateView"
                />
                <!-- /View Switcher -->
              </template>
            </business-guide-header>
            <!-- /Header --></v-app>

          <!-- Grid Wrapper -->
          <div class="grid-wrapper" :class="{ 'active': partnersView === 'grid' }">
            <el-row :gutter="20">
              <!-- Completed overlay -->
              <completed-overlay
                v-if="!canEdit || $store.state.idea.storyMode.common.completedSteps.partnerCompleted"
                @click="checkPartnerCard"
              />
              <!-- /Completed overlay -->

              <div
                v-if="isPartnersEmpty"
                class="d-flex flex-wrap"
              >
                <el-col :md="8" class="d-flex width-xl-20 mb-3">
                  <add-new-card
                    class="width-xl-20"
                    :title="$tc('addNewPartner')"
                    :disabled="hasNotAccess || aiTemplateLoader"
                    @click="dialogAddPartnerVisible = true"
                  />
                </el-col>

                <!-- Placeholder 1 -->
                <el-col v-if="$breakpoint.mdAndUp" :md="8" class="width-lg-20 mb-3">
                  <div class="placeholder-card large">
                    <div class="w-100">
                      <div class="avatar-placeholder blue" />
                      <div class="stripe-medium" />
                      <div class="stripe-small" />
                      <div class="stripe-large" />
                    </div>
                  </div>
                </el-col>
                <!-- /Placeholder 1 -->

                <!-- Placeholder 2 & Tutorial Tooltip -->
                <el-col v-if="$breakpoint.mdAndUp" :md="8" class="width-lg-20 mb-3">
                  <div class="placeholder-card large">

                    <!-- Placeholder 2 -->
                    <div class="w-100">
                      <div class="avatar-placeholder yellow" />
                      <div class="stripe-medium" />
                      <div class="stripe-small" />
                      <div class="stripe-large" />
                    </div>
                    <!-- /Placeholder 2 -->

                    <!-- Tutorial Tooltip TODO - Currently disabled in if directive -->
                    <tutorial-tooltip
                      v-if="$store.state.user.tutorial.partnerCompleted === false && 1 === 2"
                      :title="$t('pages.businessGuide.partner.addYourSalesChannel')"
                      :text="$t('pages.businessGuide.thisIsStarOfYourShow')"
                      :tutorial-name="'partnerCompleted'"
                    />
                    <!-- /Tutorial Tooltip -->

                  </div>
                </el-col>
                <!-- /Placeholder 2 & Tutorial Tooltip -->
              </div>

              <!-- Grid Items -->
              <div v-if="isPartnersEmpty === false" @click="checkPartnerCard">
                <ib-draggable
                  class="d-flex flex-wrap flex-grow-1"
                  :list="partnersToShowGrid"
                  :options="{disabled: hasNotAccess}"
                  @change="onChangePartnerOrder"
                  @start="onStartPartnerOrder"
                >
                  <template #header>
                    <el-col :md="8" class="d-flex width-xl-20 mb-3">
                      <add-new-card
                        :min-height="$breakpoint.mdAndUp ? '405px' : '164px'"
                        class="width-xl-20"
                        :title="$tc('addNewPartner')"
                        :disabled="hasNotAccess"
                        @click="dialogAddPartnerVisible = true"
                      />
                    </el-col>
                  </template>
                  <el-col
                    v-for="partner in partnersToShowGrid"
                    :key="partner.id"
                    class="width-lg-20 draggable d-flex mb-3"
                    :md="8"
                  >
                    <div
                      class="partner-card cursor-move w-100"
                      :class="{'completed-card': statusOfCompleted }"
                      @click="checkPartnerCard(partner)"
                    >

                      <!-- Card Top -->
                      <div
                        class="card-top" :class="{ 'default': !partner.image }"
                        :style="cardImageBySize(partner.image, 'medium')"
                      >

                        <!-- Actions Dropdown -->
                        <actions-dropdown
                          v-if="canEdit && !statusOfCompleted"
                          ref="actionsDropdown"
                          :entity="partner"
                          @edit="onEditPartner"
                          @copy="onCopyPartner"
                          @delete="onDeletePartner"
                        />
                      <!-- /Actions Dropdown -->

                      </div>
                      <!-- /Card Top -->

                      <!-- Card Bottom -->
                      <div class="card-bottom">

                        <!-- Title -->
                        <h3>{{ partner.name }}</h3>
                        <!-- /Title -->

                        <!-- Description -->
                        <p class="pre-formatted text-left ml-2 mr-2">
                          {{ partner.description }}
                        </p>
                      <!-- /Description -->

                      </div>
                    <!-- /Card Bottom -->

                    </div>
                  </el-col>
                </ib-draggable>
              </div>
              <!-- /Grid Items -->
            </el-row>
            <!-- Show All -->
            <el-row v-if="$store.state.idea.storyMode.concept.partner.partners.length > 4">
              <el-col>
                <show-all-toggle
                  :show="showAllPartnersGrid"
                  @click="showAllPartnersGrid = !showAllPartnersGrid"
                />
              </el-col>
            </el-row>
            <!-- /Show All -->
          </div>
          <!-- /Grid Wrapper -->

          <!-- List Wrapper -->
          <div class="list-wrapper" :class="{ 'active': partnersView === 'list' }">
            <el-row :gutter="20">
              <!-- Completed overlay -->
              <completed-overlay
                v-if="hasNotAccess"
                @click="checkPartnerCard"
              />
              <!-- /Completed overlay -->

              <ib-draggable
                class="d-flex flex-wrap flex-grow-1"
                :list="partnersToShowList"
                :options="{disabled: hasNotAccess}"
                @change="onChangePartnerOrder"
                @start="onStartPartnerOrder"
              >
                <template #header>
                  <el-col :md="12" class="d-flex mb-3">
                    <add-new-card
                      class="width-xl-20"
                      :title="$tc('addNewPartner')"
                      :disabled="hasNotAccess || aiTemplateLoader"
                      horizontal
                      @click="dialogAddPartnerVisible = true"
                    />
                  </el-col>
                </template>
                <!-- List Items -->
                <el-col
                  v-for="partner in partnersToShowList"
                  :key="partner.id"
                  :md="12"
                  class="draggable d-flex mb-3"
                >
                  <div
                    class="partner-card cursor-move w-100"
                    :class="{ 'completed-card': statusOfCompleted }"
                    @click="checkPartnerCard(partner)"
                  >
                    <!-- Card Top -->
                    <div class="card-top">

                      <!-- Card Image & Partner Name -->
                      <div class="flex-inner">

                        <!-- Card Image -->
                        <div>
                          <div
                            class="card-image-round" :class="{ 'default': !partner.image }"
                            :style="cardImageBySize(partner.image, 'small')"
                          />
                        </div>
                        <!-- /Card Image -->

                        <!-- Partner Name -->
                        <h3>{{ partner.name }}</h3>
                      <!-- /Partner Name -->

                      </div>
                      <!-- /Card Image & Partner Name -->

                      <!-- Actions Dropdown -->
                      <div class="flex-inner">

                        <!-- Actions Dropdown -->
                        <actions-dropdown
                          v-if="canEdit && !statusOfCompleted"
                          ref="actionsDropdown"
                          :entity="partner"
                          @edit="onEditPartner"
                          @copy="onCopyPartner"
                          @delete="onDeletePartner"
                        />
                      <!-- /Actions Dropdown -->

                      </div>
                    <!-- /Actions Dropdown -->

                    </div>
                    <!-- /Card Top -->

                  </div>
                </el-col>
              </ib-draggable>
              <!-- /List Items -->
            </el-row>
            <!-- Show All -->
            <el-row v-if="$store.state.idea.storyMode.concept.partner.partners.length > 5">
              <el-col>
                <show-all-toggle
                  :show="showAllPartnersList"
                  @click="showAllPartnersList = !showAllPartnersList"
                />
              </el-col>
            </el-row>
            <!-- /Show All -->
          </div>
          <!-- /List Wrapper -->

        </div>
        <!-- /Portfolio -->

      </div>
      <!-- /Content -->

      <!-- Add More Details -->
      <add-more-details
        :category="'concept'"
        :step="'partner'"
        :concept-name="'partnerMoreDetails'"
        :completed-step-name="'partnerCompleted'"
      />
      <!-- /Add More Details -->

    </div>
    <!-- /Container -->

    <!-- Main Bottom Bar -->
    <main-bottom-bar
      :back="backNavigation"
      :next="nextNavigation"
      :completed-step-name="'partnerCompleted'"
      :step-filled="$store.state.idea.storyMode.concept.partner.partners.length > 0"
      @complete-step="onCompleteStep"
    />
    <!-- /Main Bottom Bar -->

    <!-- Info Guide Drawer -->
    <info-guide-drawer
      type="partnerExamples"
      :visible="guideVisible"
      @close="guideVisible = false"
    />
    <!-- /Info Guide Drawer -->

    <!-- Dialog Add Partner -->
    <dialog-add-partner
      :dialog-add-partner-visible="dialogAddPartnerVisible"
      :partner-action="partnerAction"
      @close-dialog-add-partner="onCloseDialogAddPartner"
    />
    <!-- /Dialog Add Partner -->

    <!-- Dialog Process Completed -->
    <dialog-process-completed
      :dialog-process-completed-visible="dialogProcessCompletedVisible"
      @close-dialog-process-completed="onCloseDialogProcessCompleted"
    />
    <!-- /Dialog Process Completed -->

    <!-- Dialog Remove Examples -->
    <dialog-remove-examples
      :visible="dialogRemoveExamplesVisible"
      :loading="removeExamplesLoading"
      @no="dialogRemoveExamplesVisible = false"
      @yes="deleteExamples"
    />
    <!-- /Dialog Remove Examples -->

  </div>
  <!-- Partner -->

</template>

<script>
import ActionsDropdown from '@/views/Home/StoryMode/Components/ActionsDropdown'
import AddMoreDetails from '@/views/Home/StoryMode/Components/AddMoreDetails'
import AddNewCard from '@/components/_v2/AddNewCard'
import AiBuddyDrawer from '@/views/Home/StoryMode/Components/AiBuddy/AiBuddyDrawer.vue'
import AiTemplateBlinkingAnimation from '@/views/Home/StoryMode/Components/AiTemplateBlinkingAnimation.vue'
import BusinessGuideHeader from '@/components/_v2/Common/BusinessGuideHeader'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import BusinessPlanAdditionalInfo from '@/views/Home/StoryMode/Components/BusinessPlanAdditionalInfo.vue'
import CompletedOverlay from '@/views/Home/StoryMode/Components/CompletedOverlay'
import DashboardHeader from '@/views/Home/StoryMode/Components/DashboardHeader'
import DialogAddPartner from '@/views/Home/StoryMode/Concept/Partner/Dialogs/DialogAddPartner'
import DialogProcessCompleted from '@/views/Home/StoryMode/Components/Dialogs/DialogProcessCompleted'
import DialogRemoveExamples from '@/views/Home/StoryMode/Components/DialogRemoveExamples'
import EraseIconButton from '@/components/_v2/EraseIconButton'
import MainBottomBar from '@/views/Home/StoryMode/Components/MainBottomBar/MainBottomBar'
import MainNavigation from '@/views/Home/StoryMode/Components/MainNavigation/MainNavigation'
import MixinAiTemplate from '@/mixins/aiTemplate'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinEraseTemplate from '@/mixins/eraseTemplate'
import MixinGuide from '@/mixins/guide'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinProcessCompleted from '@/mixins/processCompleted'
import ShowAllToggle from '@/views/Home/StoryMode/Components/ShowAllToggle'
import TutorialTooltip from '@/views/Home/Components/TutorialTooltip'
import ViewSwitcher from '@/components/_v2/ViewSwitcher'

import { mapActions, mapGetters } from 'vuex'
import { getImageBySize } from '@/helpers/imageSizeHelper'

import InfoGuide from '@/views/Home/StoryMode/Components/Example/InfoGuide.vue'
import InfoGuideDrawer from '@/views/Home/StoryMode/Components/Example/InfoGuideDrawer.vue'

export default {
  name: 'Partner',

  components: {
    AiTemplateBlinkingAnimation,
    InfoGuide,
    AiBuddyDrawer,
    InfoGuideDrawer,
    BusinessPlanAdditionalInfo,
    BusinessGuideSearch,
    ActionsDropdown,
    AddMoreDetails,
    AddNewCard,
    BusinessGuideHeader,
    CompletedOverlay,
    DashboardHeader,
    DialogAddPartner,
    DialogProcessCompleted,
    DialogRemoveExamples,
    EraseIconButton,
    MainBottomBar,
    MainNavigation,
    ShowAllToggle,
    TutorialTooltip,
    ViewSwitcher
  },

  mixins: [
    MixinProcessCompleted,
    MixinGuide,
    MixinDeleteIdeaExamples,
    MixinIdeaRoles,
    MixinEraseTemplate,
    MixinAiTemplate
  ],

  data () {
    return {
      aiBuddyDrawerVisible: false,
      searchValue: '',
      removeExamplesLoading: false,
      dialogRemoveExamplesVisible: false,
      dialogAddPartnerVisible: false,
      partnerAction: null,
      guideVisible: false,
      showAllPartnersGrid: false,
      showAllPartnersList: false,
      dialogProcessCompletedVisible: false
    }
  },

  computed: {
    ...mapGetters('idea', {
      partners: 'getPartners',
      getStepIsCompleted: 'getStepIsCompleted',
      getHasItemsFromTemplate: 'getHasItemsFromTemplate'
    }),

    statusOfCompleted () {
      return this.getStepIsCompleted('partnerCompleted')
    },

    isPartnersEmpty () {
      return !this.partners.length
    },

    partnersToShowGrid () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.filterPartners
      }

      return this.showAllPartnersGrid === true
        ? this.partners
        : this.partners.slice(0, 4)
    },

    partnersToShowList () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.filterPartners
      }

      return this.showAllPartnersList === true
        ? this.partners
        : this.partners.slice(0, 5)
    },

    filterPartners () {
      return this.partners.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
    },

    partnersView () {
      return this.$store.state.itemsView.partnersView
    },

    hasNotAccess () {
      return !this.canEdit || this.statusOfCompleted
    },

    removeButtonVisible () {
      return this.canEdit && this.getHasItemsFromTemplate('getPartners')
    },

    backNavigation () {
      return {
        title: this.$t('pages.businessGuide.navigation.distribution'),
        route: 'business-guide-concept-distribution'
      }
    },

    nextNavigation () {
      return {
        title: this.$t('pages.businessGuide.navigation.market'),
        route: 'business-guide-research-market'
      }
    }
  },

  async created () {
    await this.openGuide('partnerGuide')
    await this.getPartners()
    this.$store.commit('ui/setLoader', { key: 'aiTemplate', value: false })
  },

  methods: {
    ...mapActions('idea', [
      'getPartners',
      'deleteStoryModePartners',
      'deleteIdeaExamples',
      'updateOrderNumber'
    ]),

    onStartPartnerOrder () {
      this.$refs.actionsDropdown.forEach(dropdown => {
        dropdown.$refs.itemDropdown.visible = false
      })
    },

    onChangePartnerOrder (data) {
      this.updateOrderNumber({
        type: 'partner',
        data,
        oldOrderData: this.partners,
        mutation: 'setPartners'
      })
    },

    checkPartnerCard (partner) {
      if (!this.canEdit) return
      if (this.statusOfCompleted) {
        this.dialogProcessCompletedVisible = true

        return
      }
      if (partner.id) {
        this.onEditPartner(partner)
      }
    },

    onCloseDialogAddPartner () {
      this.dialogAddPartnerVisible = false
      this.partnerAction = null
    },

    onActivateView (view) {
      this.$store.commit('itemsView/setPartnersView', view)
    },

    onEditPartner (partner) {
      this.partnerAction = Object.assign({}, partner)
      this.dialogAddPartnerVisible = true
    },

    onCopyPartner (partner) {
      this.partnerAction = Object.assign({}, partner)
      this.partnerAction.id = 0
      this.dialogAddPartnerVisible = true
    },

    onDeletePartner (partner) {
      this.deleteStoryModePartners(partner.id)

      // Return to grid view, if there are no partners
      // if (this.$store.state.idea.storyMode.concept.partner.partners.length === 0) {
      //   this.onActivateView('grid')
      // }
    },

    cardImageBySize (image, size) {
      return image ? 'background-image: url(' + getImageBySize(image.sizes, size) + ')' : ''
    },

    onGuideExampleVisible () {
      this.guideVisible = !this.guideVisible
    },

    onEraseFromTemplate () {
      if (this.statusOfCompleted) {
        this.openedDialogProcessCompleted()

        return
      }
      this.dialogRemoveExamplesVisible = true
    },

    deleteExamples () {
      this.removeExamplesLoading = true
      this.deleteIdeaExamples('partners')
        .then(response => {
          const { type, ids } = response
          this.deleteFromState({ type, ids })
          this.dialogRemoveExamplesVisible = false
          this.removeExamplesLoading = false
        }).catch(_ => { this.removeExamplesLoading = false })
    }
  }
}
</script>
